/**
 * Phase 18 — customer renders gallery.
 *
 * Thumbnail strip below the chat that lists all non-failed renders for
 * the current estimate. Status pill per thumb. Tapping opens the parent
 * Lightbox; the 3-dot menu hard-deletes a render and frees the quota.
 */

import { useEffect, useState } from "react";

import { formatRenderStatus } from "@starz/shared";
import { listRenders, deleteRender } from "@starz/shared/api/sdk.gen";
import type { RemodelRender } from "@starz/shared/api/types.gen";

interface Props {
  estimateId: string;
  /** Open the parent Lightbox at this render's image. */
  onOpenLightbox: (url: string, alt: string) => void;
  /** Bumps every time the parent wants the gallery to re-fetch (e.g.
   * after an approve/reject roundtrip). */
  refreshToken?: number;
  /** Notify parent after a delete so the quota indicator + estimate
   * row re-fetch. */
  onAfterDelete?: () => void;
}

function StatusPill({ render }: { render: RemodelRender }) {
  const colors: Record<string, string> = {
    pending: "bg-amber-50 text-amber-700",
    generating: "bg-amber-50 text-amber-700",
    complete: "bg-sky-50 text-sky-700",
    approved: "bg-emerald-50 text-emerald-700",
    rejected: "bg-rose-50 text-rose-700",
    failed: "bg-warm-gray/20 text-warm-gray",
  };
  const cls = colors[render.status] ?? "bg-warm-gray/20 text-warm-gray";
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      {formatRenderStatus(render.status)}
    </span>
  );
}

export function RendersGallery({
  estimateId,
  onOpenLightbox,
  refreshToken,
  onAfterDelete,
}: Props) {
  const [renders, setRenders] = useState<RemodelRender[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const res = await listRenders({ estimateId });
      if (cancelled) return;
      if (!("error" in res)) {
        setRenders(res.data.filter((r) => r.status !== "failed"));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId, refreshToken]);

  const handleDelete = async (renderId: string) => {
    setConfirmingId(null);
    const res = await deleteRender({ estimateId, renderId });
    if (!("error" in res)) {
      setRenders((rs) => rs.filter((r) => r.render_id !== renderId));
      onAfterDelete?.();
    }
  };

  if (loading && renders.length === 0) return null;
  if (renders.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-warm-gray">
        Renders
      </h3>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {renders.map((r) => (
          <div key={r.render_id} className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() =>
                r.url
                  ? onOpenLightbox(r.url, `Render of "${r.prompt}"`)
                  : undefined
              }
              className={`flex h-24 w-24 items-center justify-center overflow-hidden rounded-lg border border-starz-gold/20 bg-starz-cream ${
                r.url ? "cursor-pointer" : "cursor-not-allowed"
              }`}
            >
              {r.url ? (
                <img
                  src={r.url}
                  alt={`Render of "${r.prompt}"`}
                  className={`h-full w-full object-cover ${
                    r.status === "rejected" ? "opacity-50" : ""
                  }`}
                />
              ) : (
                <span className="text-[10px] italic text-warm-gray">
                  rendering…
                </span>
              )}
            </button>
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2">
              <StatusPill render={r} />
            </div>
            <button
              type="button"
              aria-label={`Delete render ${r.render_id}`}
              onClick={() => setConfirmingId(r.render_id)}
              className="absolute right-1 top-1 rounded-full bg-starz-charcoal/70 px-2 py-0.5 text-xs text-starz-off-white"
            >
              ⋯
            </button>
            {confirmingId === r.render_id ? (
              <div className="absolute right-0 top-7 z-10 flex flex-col gap-1 rounded-md border border-starz-gold/30 bg-starz-off-white p-2 shadow-md">
                <button
                  type="button"
                  onClick={() => void handleDelete(r.render_id)}
                  className="px-2 py-1 text-xs text-rose-600"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingId(null)}
                  className="px-2 py-1 text-xs text-warm-gray"
                >
                  Cancel
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
